import { clearDcConfigCache } from "%/dynamic-component-editor/helpers/dc-config-cache";
import { defineStore } from "pinia";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import FontColor from "%/dynamic-component-editor/options/font-color";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import FontStyle from "%/dynamic-component-editor/options/font-style";
import TextDecoration from "%/dynamic-component-editor/options/text-decoration";
import Padding from "%/dynamic-component-editor/options/padding";
import Margins from "%/dynamic-component-editor/options/margins";
import FontSize from "%/dynamic-component-editor/options/font-size";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import Publication from "%/dynamic-component-editor/classes/publication";
import { useStyleStore } from "@bloglovin/vue-component-library";

export const useBrandingStore = defineStore("branding-store", {
  state: () => ({
    defaultTemplateConfiguration: {},
    configuration: {},
    compiledConfiguration: {},
    publicationThemeId: null,
    currentlyInspectedBrandingElement: null,
    currentlyInspectedBrandingElementVariant: null,
    currentlyInspectedBrandingElementSize: null,
  }),
  getters: {
    themeMode() {
      return useStyleStore().getThemeMode(this.publicationThemeId);
    },
    styleId() {
      return useConfigurationStore().brandingStyleId;
    },
    styleVersionId() {
      return useConfigurationStore().brandingStyleVersionId;
    },
    getCssMappedConfiguration() {
      return this.computeCssMappedConfiguration(this.themeMode);
    },
  },
  actions: {
    computeCssMappedConfiguration(themeMode = "inherit") {
      const clonedConfig = JSON.parse(JSON.stringify(this.configuration));
      delete clonedConfig.ColorPalette;
      delete clonedConfig.TextHighlighting;
      delete clonedConfig.ButtonBranding;

      return Object.keys(clonedConfig).reduce((config, styleKey) => {
        const variants = clonedConfig[styleKey];

        const mappedVariants = Object.keys(variants).reduce((variantConfig, variantKey) => {

          const variant = variants[variantKey];

          let cssObject = {};

          if (this.compiledConfiguration[styleKey]?.[variantKey]) {
            cssObject = this.compiledConfiguration[styleKey]?.[variantKey];
            if (!cssObject) {
              cssObject = this.compiledConfiguration[this.themeMode]?.[styleKey]?.[variantKey];
            }
          } else {
            if (variants[variantKey][["default.font-color"]]) {
              cssObject["color"] = `${this.fontColor(variant, themeMode)} !important`;
            }
            if (variants[variantKey][["default.font-family"]]) {
              cssObject["font-family"] = `${this.fontFamily(variant)} !important`;
            }
            if (variants[variantKey][["default.font-weight"]]) {
              cssObject["font-weight"] = `${this.fontWeight(variant)} !important`;
            }
            if (variants[variantKey][["default.font-style"]]) {
              cssObject["font-style"] = `${this.fontStyle(variant)} !important`;
            }
            if (variants[variantKey][["default.text-decoration"]]) {
              cssObject["text-decoration"] = `${this.textDecoration(variant)} !important`;
            }
            if (variants[variantKey][["default.padding"]]) {
              cssObject["padding"] = `${this.padding(variant)} !important`;
            }
            if (variants[variantKey][["default.margins"]]) {
              cssObject["margin"] = `${this.margins(variant)} !important`;
            }
            if (variants[variantKey][["default.font-size"]]) {
              cssObject["font-size"] = `${this.fontSize(variant)} !important`;
            }
          }

          return {
            ...variantConfig,
            [variantKey]: cssObject,
          };
        }, {});

        return {
          ...config,
          [styleKey]: {
            ...mappedVariants,
          },
        };
      }, {});
    },
    paperColor() {
      return BaseOption._getStyleValuePropertiesFromTheme(
        "color.background",
        this.publicationThemeId,
        this.themeMode,
      )["paper"];
    },
    darkBorderColor() {
      return BaseOption._getStyleValuePropertiesFromTheme(
        "color.border",
        this.publicationThemeId,
        this.themeMode,
      )["dark"];
    },
    fontColor(variant, themeMode) {
      return FontColor.getValue(variant["default.font-color"], this.publicationThemeId, themeMode);
    },
    fontFamily(variant) {
      return BaseOption._getStyleValuePropertiesFromTheme("font.typeface", this.publicationThemeId)[variant["default.font-family"]];
    },
    fontWeight(variant) {
      return FontWeight.getValue(variant["default.font-weight"], this.publicationThemeId);
    },
    fontStyle(variant) {
      return FontStyle.getValue(variant["default.font-style"], this.publicationThemeId);
    },
    textDecoration(variant) {
      return TextDecoration.getValue(variant["default.text-decoration"], this.publicationThemeId);
    },
    padding(variant) {
      return Padding.getValue(variant["default.padding"], this.publicationThemeId);
    },
    margins(variant) {
      return Margins.getValue(variant["default.margins"], this.publicationThemeId);
    },
    fontSize(variant) {
      return FontSize.getValue(variant["default.font-size"], this.publicationThemeId);
    },
    setConfiguration(configuration) {
      this.configuration = configuration;
    },
    setCompiledConfiguration(configuration) {
      this.compiledConfiguration = configuration;
    },
    setDefaultTemplateConfiguration(configuration) {
      this.defaultTemplateConfiguration = configuration;
    },
    setPublicationThemeId(themeId) {
      this.publicationThemeId = themeId;
    },
    resetBrandingOption(optionKey) {
      let value;
      let templateValue;
      if (this.currentlyInspectedBrandingElement === "ButtonElement") {
        const buttonBrandingValues = this.getButtonBrandingValueAndTemplateValue();
        value = buttonBrandingValues.value;
        templateValue = buttonBrandingValues.templateValue;
      } else {
        value = this.configuration[this.currentlyInspectedBrandingElement][this.currentlyInspectedBrandingElementVariant];
        templateValue = this.defaultTemplateConfiguration[this.currentlyInspectedBrandingElement][this.currentlyInspectedBrandingElementVariant];
      }

      const updatedValue = Object.keys(value).reduce((internalValueObj, key) => {
        if (key !== optionKey) {
          return {
            ...internalValueObj,
            [key]: value[key],
          };
        } else if (Object.hasOwn(templateValue, optionKey)) {
          return {
            ...internalValueObj,
            [key]: templateValue[key],
          };
        }

        return internalValueObj;
      }, {});

      if (this.currentlyInspectedBrandingElement === "ButtonElement") {
        this.resetButtonBrandingConfig(updatedValue);
      } else {
        this.configuration[this.currentlyInspectedBrandingElement][this.currentlyInspectedBrandingElementVariant] = updatedValue;
      }
    },
    resetButtonBrandingConfig(updatedValue) {
      const buttonBrandingConfig = this.configuration["ButtonBranding"];
      buttonBrandingConfig[this.currentlyInspectedBrandingElementVariant][this.currentlyInspectedBrandingElementSize] = updatedValue;
    },
    getButtonBrandingValueAndTemplateValue() {
      const buttonBrandingConfig = this.configuration["ButtonBranding"];
      const defaultButtonBrandingConfig = this.defaultTemplateConfiguration["ButtonBranding"];

      const value = buttonBrandingConfig[this.currentlyInspectedBrandingElementVariant][this.currentlyInspectedBrandingElementSize];
      const templateValue = defaultButtonBrandingConfig[this.currentlyInspectedBrandingElementVariant][this.currentlyInspectedBrandingElementSize];
      return { value, templateValue };
    },
    updateBrandingConfigOption(updatedOption) {
      let updatedConfig;
      if (this.currentlyInspectedBrandingElement === "ButtonElement") {
        updatedConfig = this.getUpdatedButtonBrandingConfig(updatedOption);
      } else {
        updatedConfig = {
          ...this.configuration,
          [this.currentlyInspectedBrandingElement]: {
            ...this.configuration[this.currentlyInspectedBrandingElement],
            [this.currentlyInspectedBrandingElementVariant]: {
              ...this.configuration[this.currentlyInspectedBrandingElement][this.currentlyInspectedBrandingElementVariant],
              ...updatedOption,
            },
          },
        };
      }

      return updatedConfig;
    },
    getUpdatedButtonBrandingConfig(updatedOption) {
      const buttonBrandingConfig = this.configuration["ButtonBranding"];
      buttonBrandingConfig[this.currentlyInspectedBrandingElementVariant][this.currentlyInspectedBrandingElementSize] = {
        ...buttonBrandingConfig[this.currentlyInspectedBrandingElementVariant][this.currentlyInspectedBrandingElementSize],
        ...updatedOption,
      };

      return {
        ...this.configuration,
        ButtonBranding: {
          ...buttonBrandingConfig,
        },
      };
    },
    getThemeForPublication(publicationId, successCallback = () => {
    }, errorCallback = () => {
    }) {
      this.apiGet(`/branding/styles/publication/${publicationId}`, {}, response => {
        successCallback(response);
      }, errorCallback);
    },
    updateConfiguration(configuration, successCallback = () => {
    }, errorCallback = () => {
    }) {
      // Local-only update first — the FanWeb editor mutates
      // `branding-store.configuration` and assumes the legacy
      // `/branding/styles/{id}` round-trip will persist. Studio
      // doesn't populate `useConfigurationStore().brandingStyleId`
      // (it persists branding through the App-engine `Theme` entity
      // instead — see `ThemeConfigPanel.persistThemeDraft`), so the
      // POST URL becomes `/branding/styles/undefined` and the server
      // 400s with "request variable not correct type INT". When
      // there's no styleId in scope we still need the in-memory
      // configuration update so the editor and any consuming
      // computeds reflect the change; just skip the doomed POST.
      this.configuration = configuration;
      const styleId = this.styleId;
      if (styleId === null || styleId === undefined || styleId === "") {
        successCallback();
        return;
      }
      const publication = Publication.get(useFanPublicationStore().publicationId);
      this.apiPost(`/branding/styles/${styleId}`, {
        configuration: configuration,
        publication_id: publication.id,
        publication_version_id: publication.version,
        style_version_id: this.styleVersionId,
      }, response => {
        clearDcConfigCache();
        publication.version = response["publication_version_id"];
        useConfigurationStore().setBrandingStylesVersionId(response["style_version_id"]);
        successCallback();
      }, errorCallback);
    },
  },
});

export default useBrandingStore;
